import React, { useEffect, useRef, useState } from 'react';
import { keywordsCache } from '../../utils/keywordsCache';
import type { KeywordRow, TechMini } from './api';
import { ChevronRight, CloseIcon, PlusIcon } from './icons';
import { fyTrack, useImpression } from './track';

interface Props {
  keywords: KeywordRow[];
  max: number;
  weekLabel: string;
  /** Resolves '' when added, else the sentence to show. */
  onAdd: (keyword: string) => Promise<string>;
  onRemove: (k: KeywordRow) => void;
  onOpen: (k: KeywordRow, t: TechMini) => void;
  busy: boolean;
}

const InterestsSection: React.FC<Props> = ({ keywords, max, weekLabel, onAdd, onRemove, onOpen, busy }) => {
  const ref = useImpression<HTMLElement>('interests');
  const [edit, setEdit] = useState(false);
  const [pick, setPick] = useState('');
  const [chips, setChips] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const input = useRef<HTMLInputElement | null>(null);
  const canAddMore = keywords.length < max;
  const typed = pick.trim();
  const lower = new Set(keywords.map(k => k.keyword.toLowerCase()));
  const dup = typed.length > 0 && lower.has(typed.toLowerCase());
  // Keywords come only from the search engine's list: Add is enabled for an
  // exact (case-insensitive) entry of it, which the chips fill in.
  const inList = chips.some(c => c.toLowerCase() === typed.toLowerCase());

  useEffect(() => {
    let live = true;
    if (!edit || !typed) { setChips([]); return; }
    const t = setTimeout(() => {
      keywordsCache.searchKeywords(typed, 12).then(list => {
        if (live) setChips(list.filter(k => !lower.has(k.toLowerCase())).slice(0, 8));
      }).catch(() => { if (live) setChips([]); });
    }, 120);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, edit, keywords.length]);

  const toggleEdit = () => {
    fyTrack('foryou_keyword_edit', { on: !edit, count: keywords.length });
    setEdit(!edit); setPick(''); setErr('');
  };
  const add = async (value?: string) => {
    const k = (value ?? typed).trim();
    if (!k || dup || !canAddMore || busy) return;
    const msg = await onAdd(k);
    if (msg) { setErr(msg); return; }
    setPick(''); setErr('');
    setTimeout(() => input.current?.focus(), 0);
  };

  return (
    <section ref={ref} className="fy-sec" aria-label="Your interests">
      <div className="fy-sec-head">
        <h2 className="fy-h2">Your interests · featured this week</h2>
        {(keywords.length > 0 || edit) && (
          <button type="button" className="fy-btn fy-btn-quiet fy-btn-sm" onClick={toggleEdit}>
            {edit ? 'Done' : 'Edit interests'}
          </button>
        )}
      </div>
      <div className="fy-muted">
        {keywords.length} of {max} keywords · one featured technology per keyword, every week
      </div>

      {keywords.length > 0 && (
        <div className="fy-list">
          {keywords.map(k => (
            <article key={k.keyword_norm} className="fy-kcard">
              <div className="fy-khead">
                <span className="fy-dot" />
                <h3 className="fy-h3">{k.keyword}</h3>
                {!edit && <span className="fy-week">{k.week_label || weekLabel}</span>}
                {edit && (
                  <button type="button" className="fy-btn fy-btn-quiet fy-btn-sm" style={{ marginLeft: 'auto' }}
                          disabled={busy} onClick={() => onRemove(k)} aria-label={`Remove ${k.keyword}`}>
                    <CloseIcon size={14} /> Remove
                  </button>
                )}
              </div>
              <Featured k={k} onOpen={onOpen} />
            </article>
          ))}
        </div>
      )}

      {edit && canAddMore && (
        <div className="fy-addbox">
          <label className="fy-label" htmlFor="fy-add-interest">Add a keyword</label>
          <div className="fy-row" style={{ flexWrap: 'nowrap' }}>
            <input id="fy-add-interest" ref={input} type="text" className="fy-input" maxLength={120} value={pick}
                   autoComplete="off" placeholder="Search keywords, e.g. Computer Vision"
                   onChange={e => { setPick(e.target.value); setErr(''); }}
                   onKeyDown={e => { if (e.key === 'Enter' && inList) { e.preventDefault(); void add(); } }} />
            <button type="button" className="fy-btn fy-btn-primary" onClick={() => add()}
                    disabled={!typed || dup || !inList || busy}>Add</button>
          </div>
          {dup && <div className="fy-err">That keyword is already in your interests.</div>}
          {!dup && err && <div className="fy-err" role="alert">{err}</div>}
          {!dup && typed && !inList && chips.length === 0 && (
            <div className="fy-muted">No keyword in the Cosmos list matches “{typed}”.</div>
          )}
          {chips.length > 0 && (
            <>
              <div className="fy-muted">Suggested keywords</div>
              <div className="fy-chips">
                {chips.map(c => (
                  <button key={c} type="button" className="fy-chip-btn" onClick={() => setPick(c)}>{c}</button>
                ))}
              </div>
            </>
          )}
          <div className="fy-muted">You'll see one featured technology for each keyword, every week.</div>
        </div>
      )}
      {edit && !canAddMore && (
        <div className="fy-note">You've reached {max} keywords. Remove one to add another.</div>
      )}
      {!edit && canAddMore && (
        <div>
          <button type="button" className="fy-btn fy-btn-outline" onClick={() => {
            fyTrack('foryou_keyword_edit', { on: true, count: keywords.length, from: 'add_button' });
            setEdit(true); setPick('');
            setTimeout(() => input.current?.focus(), 0);
          }}>
            <PlusIcon /> Add keyword
          </button>
        </div>
      )}
    </section>
  );
};

const Featured: React.FC<{ k: KeywordRow; onOpen: (k: KeywordRow, t: TechMini) => void }> = ({ k, onOpen }) => {
  const t = k.tech;
  // Just added: its pool is being built. Shown as loading for a while; a
  // keyword still pending long after (a failed or skipped build) falls back
  // to the plain note rather than spinning for ever.
  const fresh = !k.added_at || Date.now() - Date.parse(k.added_at) < 30 * 60_000;
  if (k.state === 'pending' && fresh) {
    return (
      <div className="fy-feat is-static" role="status" aria-busy="true">
        <div style={{ flex: 1 }}>
          <div className="fy-feat-label">Featured tech</div>
          <div className="fy-muted" style={{ marginTop: 2 }}>Finding this week's tech offer…</div>
          <div className="fy-skel" style={{ minHeight: 14, marginTop: 8, borderRadius: 6 }} />
        </div>
      </div>
    );
  }
  if (k.state !== 'ok' || !t) {
    return (
      <div className="fy-feat is-static">
        <div style={{ flex: 1 }}>
          <div className="fy-feat-label">Featured tech</div>
          <div className="fy-muted" style={{ marginTop: 2 }}>
            No featured technology for this keyword yet.
            {k.state === 'pending' ? ' We are looking for matches now.' : ''}
          </div>
        </div>
      </div>
    );
  }
  if (!t.available) {
    return (
      <div className="fy-feat is-static">
        <div style={{ flex: 1 }}>
          <div className="fy-feat-label">Featured tech</div>
          <div className="fy-muted" style={{ marginTop: 2 }}>
            This week's technology is no longer available. A new one arrives with next week's pick.
          </div>
        </div>
      </div>
    );
  }
  const src = [t.university, t.area].filter(Boolean).join(' · ');
  return (
    <button type="button" className="fy-feat" onClick={() => onOpen(k, t)} aria-label={`Open ${t.title}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fy-feat-label">Featured tech</div>
        <div className="fy-feat-title">{t.title}</div>
        {src && <div className="fy-muted">{src}</div>}
      </div>
      <span className="fy-icon-btn" aria-hidden="true"><ChevronRight /></span>
    </button>
  );
};

export default InterestsSection;
